/** Collision tests for characters against grids, ships, suns and the shell, and the swept mover. */
import { CHARACTER } from "../constants";
import { boxesOverlap, clamp, v2, type Box, type Vec2 } from "../math";
import { T, isSolid, tileOutline, type Grid, type TileId } from "../tiles";
import { tileShape } from "../shapes";

/** A square tile's outline. */
const SQUARE = tileOutline(0, 0);
import { type ShipBody, type Sim, asteroidBox, worldToShip, shipCenter } from "./ships";
import { upVector, rightVector, characterBox } from "./character";

// --------------------------------------------------------------- collision

export const notEmpty = (t: TileId) => t !== T.EMPTY;

/** Does `box` (in the grid's coordinates offset by ox, oy) overlap any tile matching `pred`? */
export function boxHitsGrid(box: Box, grid: Grid, ox: number, oy: number, pred: (t: TileId) => boolean): boolean {
  const x0 = Math.max(0, Math.floor(box.x0 - ox));
  const y0 = Math.max(0, Math.floor(box.y0 - oy));
  const x1 = Math.min(grid.w - 1, Math.ceil(box.x1 - ox) - 1);
  const y1 = Math.min(grid.h - 1, Math.ceil(box.y1 - oy) - 1);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (pred(grid.tiles[y * grid.w + x]!)) return true;
    }
  }
  return false;
}

/** Does a circle at (cx, cy) overlap any matching tile of a grid anchored at (ox, oy)? */
export function circleHitsGrid(cx: number, cy: number, r: number, grid: Grid, ox: number, oy: number, pred: (t: TileId) => boolean): boolean {
  const x0 = Math.max(0, Math.floor(cx - r - ox));
  const y0 = Math.max(0, Math.floor(cy - r - oy));
  const x1 = Math.min(grid.w - 1, Math.floor(cx + r - ox));
  const y1 = Math.min(grid.h - 1, Math.floor(cy + r - oy));
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (!pred(grid.tiles[y * grid.w + x]!)) continue;
      const nx = clamp(cx, ox + x, ox + x + 1);
      const ny = clamp(cy, oy + y, oy + y + 1);
      if ((nx - cx) ** 2 + (ny - cy) ** 2 < r * r) return true;
    }
  }
  return false;
}

export function circleHitsBox(cx: number, cy: number, r: number, box: Box): boolean {
  const nx = clamp(cx, box.x0, box.x1);
  const ny = clamp(cy, box.y0, box.y1);
  return (nx - cx) ** 2 + (ny - cy) ** 2 < r * r;
}

export function outsideShell(box: Box, radius: number): boolean {
  const fx = Math.max(Math.abs(box.x0), Math.abs(box.x1));
  const fy = Math.max(Math.abs(box.y0), Math.abs(box.y1));
  return Math.hypot(fx, fy) > radius;
}

/** Does a world-space box overlap a ship's solid tiles? Tested in ship space on the box's rotated bounds. */
export function boxHitsShip(box: Box, ship: ShipBody): boolean {
  const c = shipCenter(ship);
  if (!circleHitsBox(c.x, c.y, ship.stats.radius, box)) return false;
  const corners = [v2(box.x0, box.y0), v2(box.x1, box.y0), v2(box.x1, box.y1), v2(box.x0, box.y1)].map((q) =>
    worldToShip(ship, q),
  );
  const local = {
    x0: Math.min(...corners.map((q) => q.x)),
    y0: Math.min(...corners.map((q) => q.y)),
    x1: Math.max(...corners.map((q) => q.x)),
    y1: Math.max(...corners.map((q) => q.y)),
  };
  return boxHitsGrid(local, ship.grid, 0, 0, isSolid);
}

/**
 * Does a character's body (standing at `feet`, turned by `up`) overlap a
 * solid tile of an asteroid's grid anchored at (ox, oy)? Ground tiles have
 * their real shape (the smooth surface through them, see groundShape),
 * shaped blocks theirs (see shapes.ts), and the body's bottom corners are
 * bevelled (CHARACTER.footBevel), so feet settle onto slopes and walking up
 * a run of one-block steps is smooth.
 */
export function bodyHitsGrid(feet: Vec2, up: number, grid: Grid, ox: number, oy: number): boolean {
  const box = characterBox(feet, up);
  const R = rightVector(up);
  const U = upVector(up);
  const hw = CHARACTER.width / 2;
  const b = CHARACTER.footBevel;
  const at = (r: number, u: number) => v2(feet.x + R.x * r + U.x * u, feet.y + R.y * r + U.y * u);
  const body = [at(-hw + b, 0), at(hw - b, 0), at(hw, b), at(hw, CHARACTER.height), at(-hw, CHARACTER.height), at(-hw, b)];
  const x0 = Math.max(0, Math.floor(box.x0 - ox));
  const y0 = Math.max(0, Math.floor(box.y0 - oy));
  const x1 = Math.min(grid.w - 1, Math.ceil(box.x1 - ox) - 1);
  const y1 = Math.min(grid.h - 1, Math.ceil(box.y1 - oy) - 1);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      // A solid tile is its shape or a square; an open one may hold slivers of the smooth ground next door (see TileShape).
      const shape = tileShape(grid, x, y);
      const pieces = shape ? shape.pieces : isSolid(grid.tiles[y * grid.w + x]!) ? [SQUARE] : null;
      if (!pieces) continue;
      for (const piece of pieces) {
        if (convexOverlap(body, piece.map((p) => v2(ox + x + p.x, oy + y + p.y)))) return true;
      }
    }
  }
  return false;
}

/**
 * Does an upright character box (in a ship's own grid) overlap any of its
 * solid tiles? Shaped blocks (see shapes.ts) collide as their real shape, so
 * you slide down a sloped bow instead of standing on its corner, and walk up
 * a gentle one.
 */
export function boxHitsShipTiles(box: Box, grid: Grid): boolean {
  const x0 = Math.max(0, Math.floor(box.x0));
  const y0 = Math.max(0, Math.floor(box.y0));
  const x1 = Math.min(grid.w - 1, Math.ceil(box.x1) - 1);
  const y1 = Math.min(grid.h - 1, Math.ceil(box.y1) - 1);
  let poly: Vec2[] | null = null;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const solid = isSolid(grid.tiles[y * grid.w + x]!);
      const shape = tileShape(grid, x, y);
      if (!shape) {
        if (solid) return true;
        continue;
      }
      poly ??= [v2(box.x0, box.y0), v2(box.x1, box.y0), v2(box.x1, box.y1), v2(box.x0, box.y1)];
      for (const piece of shape.pieces) if (convexOverlap(poly, piece.map((p) => v2(x + p.x, y + p.y)))) return true;
    }
  }
  return false;
}

/** Do two convex polygons overlap (by more than touching)? Separating axis test over both polygons' edges. */
export function convexOverlap(a: Vec2[], b: Vec2[]): boolean {
  const EPS = 1e-6;
  for (const poly of [a, b]) {
    for (let i = 0; i < poly.length; i++) {
      const p = poly[i]!;
      const q = poly[(i + 1) % poly.length]!;
      const len = Math.hypot(q.x - p.x, q.y - p.y);
      if (len < 1e-9) continue;
      const nx = (q.y - p.y) / len;
      const ny = (p.x - q.x) / len;
      let aMin = Infinity;
      let aMax = -Infinity;
      for (const v of a) {
        const d = v.x * nx + v.y * ny;
        aMin = Math.min(aMin, d);
        aMax = Math.max(aMax, d);
      }
      let bMin = Infinity;
      let bMax = -Infinity;
      for (const v of b) {
        const d = v.x * nx + v.y * ny;
        bMin = Math.min(bMin, d);
        bMax = Math.max(bMax, d);
      }
      if (aMax <= bMin + EPS || bMax <= aMin + EPS) return false;
    }
  }
  return true;
}

/** Collision for a character in world space (not aboard a ship). */
export function blockedForCharacter(feet: Vec2, up: number, sim: Sim): boolean {
  const box = characterBox(feet, up);
  if (outsideShell(box, sim.world.worldRadius)) return true;
  for (const s of sim.world.suns) if (circleHitsBox(s.position.x, s.position.y, s.radius, box)) return true;
  if (rockHitsCharacter(feet, up, sim)) return true;
  for (const ship of sim.ships.values()) if (boxHitsShip(box, ship)) return true;
  return false;
}

/** Whether a character's body at `feet` would be in asteroid rock (not ships, suns or the shell). */
export function rockHitsCharacter(feet: Vec2, up: number, sim: Sim): boolean {
  const box = characterBox(feet, up);
  return sim.asteroids.some((a) => boxesOverlap(box, asteroidBox(a.def)) && bodyHitsGrid(feet, up, a.grid, a.def.ox, a.def.oy));
}

export const MAX_SUBSTEP = 0.25;

/**
 * Move `d` along the unit vector `dir` in small steps, stopping flush against
 * whatever `blocked` reports. If we start out already overlapping something (a
 * tile was just built on us), movement is left unchecked so we can escape.
 */
export function sweep(start: Vec2, dir: Vec2, d: number, blocked: (p: Vec2) => boolean): { pos: Vec2; hit: boolean } {
  const shift = (p: Vec2, s: number) => v2(p.x + dir.x * s, p.y + dir.y * s);
  if (d === 0 || blocked(start)) return { pos: shift(start, d), hit: false };
  const steps = Math.ceil(Math.abs(d) / MAX_SUBSTEP);
  const step = d / steps;
  let p = start;
  for (let i = 0; i < steps; i++) {
    const next = shift(p, step);
    if (!blocked(next)) {
      p = next;
      continue;
    }
    let lo = 0;
    let hi = 1;
    for (let k = 0; k < 8; k++) {
      const mid = (lo + hi) / 2;
      if (blocked(shift(p, step * mid))) hi = mid;
      else lo = mid;
    }
    return { pos: shift(p, step * lo), hit: true };
  }
  return { pos: p, hit: false };
}
